import React, { useState, useEffect } from 'react';
import { StoreBlackout, StoreBlackoutCreate, StoreBlackoutUpdate, BlackoutType, BLACKOUT_TYPE_LABELS } from '../../../types/site';
import { siteService } from '../../../services/siteService';
import { authService } from '../../../services/auth/authService';
import Modal from '../../shared/Modal';
import { formatDate } from '../../../utils/dateUtils';
import { isBlackoutCurrentlyActive, validateBlackoutDraft } from '../../../utils/blackoutUtils';

const BLACKOUT_TYPES: BlackoutType[] = [
  'maintenance', 'seasonal', 'emergency', 'weather', 'other'
];

const StoreBlackoutsCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [blackouts, setBlackouts] = useState<StoreBlackout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBlackout, setEditingBlackout] = useState<StoreBlackout | null>(null);
  const [editForm, setEditForm] = useState({
    blackout_type: '' as BlackoutType,
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    active: true
  });
  const [saving, setSaving] = useState(false);

  // Add modal state
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState({
    blackout_type: 'maintenance' as BlackoutType,
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    active: true
  });

  const storeId = authService.getStoreId();

  useEffect(() => {
    loadBlackouts();
  }, []);

  const loadBlackouts = async () => {
    try {
      setLoading(true);
      // Pass activeOnly=false to show both active and inactive blackouts
      const data = await siteService.getStoreBlackouts(undefined, false);
      setBlackouts(data);
      setError(null);
    } catch (err) {
      setError('Failed to load blackout dates');
      console.error('Error loading store blackouts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blackoutId: number) => {
    if (!window.confirm('Are you sure you want to delete this blackout period?')) {
      return;
    }
    try {
      await siteService.deleteStoreBlackout(blackoutId);
      await loadBlackouts();
    } catch (err) {
      console.error('Error deleting blackout:', err);
    }
  };

  const getBlackoutTypeLabel = (type: string): string => {
    return BLACKOUT_TYPE_LABELS[type as keyof typeof BLACKOUT_TYPE_LABELS] || type;
  };

  const formatDateForInput = (dateStr: string): string => {
    return dateStr.split('T')[0];
  };

  const handleEdit = (blackout: StoreBlackout) => {
    setEditingBlackout(blackout);
    setEditForm({
      blackout_type: blackout.blackout_type,
      title: blackout.title,
      description: blackout.description || '',
      start_date: formatDateForInput(blackout.start_date),
      end_date: formatDateForInput(blackout.end_date),
      active: blackout.active
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingBlackout) return;

    const validationError = validateBlackoutDraft(editForm);
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setSaving(true);
      const updateData: StoreBlackoutUpdate = {
        blackout_type: editForm.blackout_type,
        title: editForm.title.trim(),
        description: editForm.description.trim() || undefined,
        start_date: editForm.start_date,
        end_date: editForm.end_date,
        active: editForm.active
      };
      await siteService.updateStoreBlackout(editingBlackout.id, updateData);
      await loadBlackouts();
      setEditModalOpen(false);
      setEditingBlackout(null);
    } catch (err) {
      console.error('Error updating blackout:', err);
      alert('Failed to update blackout. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingBlackout(null);
  };

  const handleOpenAddModal = () => {
    setAddForm({
      blackout_type: 'maintenance',
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      active: true
    });
    setAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
  };

  const handleSaveAdd = async () => {
    const validationError = validateBlackoutDraft(addForm);
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setSaving(true);
      const createData: StoreBlackoutCreate = {
        store_id: storeId,
        blackout_type: addForm.blackout_type,
        title: addForm.title.trim(),
        description: addForm.description.trim() || undefined,
        start_date: addForm.start_date,
        end_date: addForm.end_date,
        active: addForm.active
      };
      await siteService.createStoreBlackout(createData);
      await loadBlackouts();
      setAddModalOpen(false);
    } catch (err) {
      console.error('Error creating blackout:', err);
      alert('Failed to create blackout. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="config-card">
      <div
        className="config-card-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="config-card-title">
          <span className="config-card-icon">📅</span>
          <h2>Store Blackout Dates</h2>
        </div>
        <div className="config-card-header-right">
          <span className="config-card-db-label">campground_db/store_blackouts</span>
          <span className={`config-card-toggle ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </div>
      </div>

      {isExpanded && (
        <div className="config-card-content">
          {loading ? (
            <div className="config-loading">Loading blackout dates...</div>
          ) : error ? (
            <div className="config-error">{error}</div>
          ) : blackouts.length === 0 ? (
            <div className="config-empty">
              <p>No store-wide blackout dates configured.</p>
              <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>+ Add Blackout Period</button>
            </div>
          ) : (
            <>
              <table className="config-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blackouts.map(blackout => (
                    <tr key={blackout.id} className={isBlackoutCurrentlyActive(blackout) ? 'row-active-period' : ''}>
                      <td title={blackout.description || undefined}>{blackout.title}</td>
                      <td>{getBlackoutTypeLabel(blackout.blackout_type)}</td>
                      <td>{formatDate(blackout.start_date)}</td>
                      <td>{formatDate(blackout.end_date)}</td>
                      <td>
                        {isBlackoutCurrentlyActive(blackout) ? (
                          <span className="status-badge status-active-now">Active Now</span>
                        ) : blackout.active ? (
                          <span className="status-active">Scheduled</span>
                        ) : (
                          <span className="status-inactive">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="config-actions">
                          <button
                            className="btn btn-text btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(blackout);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-text btn-sm btn-danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(blackout.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="config-card-footer">
                <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>+ Add Blackout Period</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={handleCloseEditModal}
        title="Edit Blackout Period"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseEditModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="blackout-title">Title *</label>
            <input
              id="blackout-title"
              type="text"
              value={editForm.title}
              onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter blackout title"
            />
          </div>
          <div className="form-group">
            <label htmlFor="blackout-type">Blackout Type *</label>
            <select
              id="blackout-type"
              value={editForm.blackout_type}
              onChange={(e) => setEditForm(prev => ({ ...prev, blackout_type: e.target.value as BlackoutType }))}
            >
              {BLACKOUT_TYPES.map(type => (
                <option key={type} value={type}>
                  {BLACKOUT_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="blackout-start">Start Date *</label>
            <input
              id="blackout-start"
              type="date"
              value={editForm.start_date}
              onChange={(e) => setEditForm(prev => ({ ...prev, start_date: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor="blackout-end">End Date *</label>
            <input
              id="blackout-end"
              type="date"
              value={editForm.end_date}
              onChange={(e) => setEditForm(prev => ({ ...prev, end_date: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor="blackout-description">Description</label>
            <textarea
              id="blackout-description"
              value={editForm.description}
              onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter description (optional)"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={editForm.active}
                onChange={(e) => setEditForm(prev => ({ ...prev, active: e.target.checked }))}
              />
              Active
            </label>
            <small className="form-help">Inactive blackouts will not block reservations.</small>
          </div>
        </div>
      </Modal>

      {/* Add Modal */}
      <Modal
        isOpen={addModalOpen}
        onClose={handleCloseAddModal}
        title="Add Blackout Period"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseAddModal} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSaveAdd} disabled={saving}>
              {saving ? 'Adding...' : 'Add Blackout'}
            </button>
          </>
        }
      >
        <div className="edit-form">
          <div className="form-group">
            <label htmlFor="add-blackout-title">Title *</label>
            <input
              id="add-blackout-title"
              type="text"
              value={addForm.title}
              onChange={(e) => setAddForm(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter blackout title"
            />
          </div>
          <div className="form-group">
            <label htmlFor="add-blackout-type">Blackout Type *</label>
            <select
              id="add-blackout-type"
              value={addForm.blackout_type}
              onChange={(e) => setAddForm(prev => ({ ...prev, blackout_type: e.target.value as BlackoutType }))}
            >
              {BLACKOUT_TYPES.map(type => (
                <option key={type} value={type}>
                  {BLACKOUT_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="add-blackout-start">Start Date *</label>
            <input
              id="add-blackout-start"
              type="date"
              value={addForm.start_date}
              onChange={(e) => setAddForm(prev => ({ ...prev, start_date: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor="add-blackout-end">End Date *</label>
            <input
              id="add-blackout-end"
              type="date"
              value={addForm.end_date}
              onChange={(e) => setAddForm(prev => ({ ...prev, end_date: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label htmlFor="add-blackout-description">Description</label>
            <textarea
              id="add-blackout-description"
              value={addForm.description}
              onChange={(e) => setAddForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter description (optional)"
              rows={3}
            />
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={addForm.active}
                onChange={(e) => setAddForm(prev => ({ ...prev, active: e.target.checked }))}
              />
              Active
            </label>
            <small className="form-help">Set as active to immediately block reservations for this period.</small>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default StoreBlackoutsCard;
